// MediaPicker.tsx
import { useCallback, useEffect, useRef, useState } from "react";
import type { ChangeEvent } from "react";
import { useDispatch } from "react-redux";
import type { AppDispatch } from "../../store";
import {
  fetchStudioUploads,
  uploadStudioAsset,
} from "../../features/studio-controller/StudioServices";
import type { MediaItem, StudioUploadsResponse } from "../../types/studioTypes";
import { Icon } from "./Icon";

type MediaType = "IMAGE" | "VIDEO";

interface MediaPickerProps {
  type?: MediaType;
  onPick?: (item: MediaItem) => void;
  busy?: boolean;
  compact?: boolean;
  selectedId?: number | string | null;
}

const ACCEPT: Record<MediaType, string> = {
  IMAGE: "image/jpeg,image/png,image/webp",
  VIDEO: "video/mp4,video/quicktime,video/3gpp,video/webm",
};

const errorText = (e: unknown, fallback: string): string =>
  typeof e === "string" ? e : (e as { message?: string })?.message || fallback;

const cx = (...classes: (string | false | null | undefined)[]) => classes.filter(Boolean).join(" ");

/** The client's Gallery as a grid, with an upload button. Uploads go to the Gallery too. */
export function MediaPicker({
  type = "IMAGE",
  onPick,
  busy = false,
  compact = false,
  selectedId = null,
}: MediaPickerProps) {
  const dispatch = useDispatch<AppDispatch>();
  const pageSize = compact ? 12 : 30;

  const [items, setItems] = useState<MediaItem[] | null>(null);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const load = useCallback(
    async (p: number) => {
      try {
        const data = (await dispatch(
          fetchStudioUploads({ type, page: p, size: pageSize })
        ).unwrap()) as StudioUploadsResponse;
        const list = data?.results || [];
        setItems((prev) => (p === 0 ? list : [...(prev || []), ...list]));
        const total = data?.page?.total ?? list.length;
        setHasMore((p + 1) * pageSize < total);
        setPage(p);
      } catch (e) {
        setError(errorText(e, "Could not load your Gallery"));
        setItems((prev) => prev || []);
      }
    },
    [dispatch, type, pageSize]
  );

  useEffect(() => {
    load(0);
  }, [load]);

  async function upload(file?: File) {
    if (!file) return;
    setUploading(true);
    setError(null);
    try {
      const item = (await dispatch(uploadStudioAsset(file)).unwrap()) as MediaItem;
      setItems((prev) => [item, ...(prev || [])]);
      onPick?.(item);
    } catch (e) {
      setError(errorText(e, "Upload failed"));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  const onFileChange = (e: ChangeEvent<HTMLInputElement>) => upload(e.target.files?.[0]);

  const uploadLabel = uploading
    ? "Uploading…"
    : type === "VIDEO"
      ? "Upload a video"
      : "Upload a photo";

  return (
    <div className="flex flex-col gap-3">
      <button
        type="button"
        disabled={uploading}
        onClick={() => fileRef.current?.click()}
        className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-st-green px-3 py-2 text-sm font-semibold text-white hover:bg-st-green-dark disabled:opacity-50"
      >
        <Icon name="upload" size={18} /> {uploadLabel}
      </button>

      <input ref={fileRef} type="file" accept={ACCEPT[type]} hidden onChange={onFileChange} />

      <p className="text-xs leading-relaxed text-st-muted">
        {type === "VIDEO"
          ? "MP4, MOV, 3GP or WebM up to 200 MB."
          : "JPG, PNG or WebP up to 10 MB."}{" "}
        Uploads are kept in your Gallery.
      </p>

      {error && (
        <p role="alert" className="text-sm text-st-red">
          {error}
        </p>
      )}

      {items === null && (
        <div className="grid md:grid-cols-3 gap-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <span key={i} className="aspect-square animate-pulse rounded-lg bg-st-line" />
          ))}
        </div>
      )}

      {items && items.length === 0 && !error && (
        <p className="text-sm text-st-muted">
          {type === "VIDEO" ? "No videos in your Gallery yet." : "No photos in your Gallery yet."}
        </p>
      )}

      {items && items.length > 0 && (
        <div className={cx("grid md:grid-cols-2 gap-2", busy && "pointer-events-none opacity-60")}>
          {items.map((it) => {
            const on = selectedId === it.id;
            const label = it.templateName || it.fileName || "Gallery item";
            return (
              <button
                key={it.id}
                type="button"
                title={label}
                disabled={busy}
                onClick={() => onPick?.(it)}
                className={cx(
                  "relative aspect-square overflow-hidden rounded-lg border-2 bg-st-paper hover:border-st-green disabled:cursor-not-allowed",
                  on ? "border-st-green ring-2 ring-st-green-soft" : "border-transparent"
                )}
              >
                {type === "VIDEO" ? (
                  <>
                    <video
                      src={`${it.mediaUrl}#t=0.5`}
                      preload="metadata"
                      muted
                      playsInline
                      className="h-full w-full object-cover"
                    />
                    <span className="absolute bottom-1 right-1 grid h-5 w-5 place-items-center rounded-full bg-st-night/80 text-white">
                      <Icon name="play" size={12} />
                    </span>
                  </>
                ) : (
                  <img
                    src={it.mediaUrl}
                    alt={label}
                    loading="lazy"
                    className="h-full w-full object-cover"
                  />
                )}
              </button>
            );
          })}
        </div>
      )}

      {hasMore && (
        <button
          type="button"
          onClick={() => load(page + 1)}
          className="self-center text-sm font-medium text-st-green hover:text-st-green-dark"
        >
          Show more
        </button>
      )}
    </div>
  );
}